<template>
  <div>
    <div id="editor"></div>
    <!-- Knobs lint (spec 2026-10-03-stellschrauben §8.2): for everyone who may edit the CSS, only hints,
         saving always works. A click jumps to the line. -->
    <div v-if="hinweise.length || ausnahmen" class="css-lint" data-test="css-lint">
      <div class="css-lint-kopf">
        <span>{{ $t('settings.customCss.lint.titel', { n: hinweise.length }) }}</span>
        <span v-if="ausnahmen" class="css-lint-ausnahmen">{{ $t('settings.customCss.lint.ausnahmen', { n: ausnahmen }) }}</span>
      </div>
      <div class="css-lint-liste">
        <button
          v-for="(h, i) in hinweise"
          :key="`${i}-${h.code}-${h.line}`"
          type="button"
          class="css-lint-hinweis"
          :class="`is-${h.level}`"
          :data-code="h.code"
          data-test="css-lint-hinweis"
          @click="zurZeile(h.line)"
        >
          <span class="css-lint-zeile">{{ $t('settings.customCss.lint.zeile', { n: h.line }) }}</span>
          <span class="css-lint-text">{{ text(h) }}</span>
        </button>
      </div>
      <p class="css-lint-fuss">{{ $t('settings.customCss.lint.fuss') }}</p>
    </div>
  </div>
</template>

<script>
import CodeFlask from 'codeflask';

let lint = null;
/** knobsLint with its two manifests, loaded once and lazily (postcss is big). */
function lintLaden() {
  if (lint) return lint;
  lint = Promise.all([
    import('@/editor-v2/utils/knobsLint'),
    import('@/editor-v2/tokens/wm.knobs.json'),
    import('@/editor-v2/tokens/wm.tokens.json'),
  ]).then(([mod, knobs, tokens]) => {
    const vocab = mod.knobsVocabulary(knobs.default || knobs, tokens.default || tokens);
    return { vocab, pruefen: (css) => mod.lintKnobs(css, vocab) };
  });
  return lint;
}

export default {
  name: 'CustomCssEditor',
  props: {
    cssCode: {
      type: String,
      default: null,
    },
  },
  data() {
    return { befunde: [], vocab: null };
  },
  computed: {
    hinweise() {
      return this.befunde.filter((b) => b.code !== 'A');
    },
    ausnahmen() {
      return this.befunde.filter((b) => b.code === 'A').length;
    },
  },
  mounted() {
    const flask = new CodeFlask('#editor', {
      language: 'css',
      lineNumbers: true,
    });
    this.flask = flask;
    flask.updateCode(this.cssCode || '');
    flask.onUpdate((code) => {
      this.$emit('update-cssCode', code);
      clearTimeout(this.lintTimer);
      this.lintTimer = setTimeout(() => this.pruefen(code), 400);
    });
    this.pruefen(this.cssCode || '');
  },
  watch: {
    // The parent may change the CSS too ("CI von Webseite" writes its knob block): the editor shows it.
    cssCode(code) {
      if (this.flask && (code || '') !== this.flask.getCode()) this.flask.updateCode(code || '');
    },
  },
  beforeDestroy() {
    clearTimeout(this.lintTimer);
  },
  methods: {
    async pruefen(css) {
      try {
        const { vocab, pruefen } = await lintLaden();
        this.vocab = vocab;
        this.befunde = css.trim() ? pruefen(css) : [];
      } catch (e) {
        // The lint only helps; without it the field works as before.
        this.befunde = [];
      }
    },
    text(h) {
      const prop = h.prop || '';
      const wert = { prop, ersatz: h.suggestion || '', selektor: h.selector };
      if (h.code === 'W4' && !h.suggestion) return this.$t('settings.customCss.lint.W4ohne', wert);
      if (h.code === 'I1') {
        const info = this.vocab && this.vocab.public.get(prop);
        return this.$t('settings.customCss.lint.I1', { ...wert, wann: info ? info.applies_when : '' });
      }
      return this.$t(`settings.customCss.lint.${h.code}`, wert);
    },
    /** Puts the caret at the start of the line and scrolls it into view. */
    zurZeile(line) {
      const ta = this.flask && this.flask.elTextarea;
      if (!ta || !line) return;
      const pos = ta.value.split('\n').slice(0, line - 1).reduce((n, l) => n + l.length + 1, 0);
      ta.focus();
      ta.setSelectionRange(pos, pos);
      const zeilenHoehe = parseFloat(window.getComputedStyle(ta).lineHeight) || 20;
      ta.scrollTop = Math.max(0, (line - 4) * zeilenHoehe);
    },
  },
};
</script>

<style scoped lang="scss">
#editor {
  // Own positioning box: codeflask is absolute and would otherwise grow over the lint list below (bootstrap .col is relative).
  position: relative;
  height: 500px;
  width: 100%;
  border-radius: 3px;
  box-shadow: 0 2px 2px 0 rgba(#000, .14),
  0 1px 5px 0 rgba(#000, .12), 0 3px 1px -2px rgba(#000, .2);
  ::v-deep  .codeflask {
    height: 100%;
    border-radius: 3px;
    width: calc(100% - 30px);
    z-index: 3;
    &::before, .codeflask__lines {
      background: #FFF;
      width: 40px;
    }
    .codeflask__textarea {
      left: 40px;
      background-color: #fff;
    }
    .codeflask__code {
      overflow: hidden;
    }
  }
}

.css-lint {
  margin-top: 16px;
  border: 1px solid #e3e6eb;
  border-radius: 6px;
  background: #fff;
  font-size: 13px;
}

.css-lint-kopf {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f3f6;
  color: #1f2430;
  font-weight: 600;
}

.css-lint-ausnahmen {
  color: #8a919c;
  font-weight: 400;
}

.css-lint-liste {
  max-height: 336px;
  overflow-y: auto;
}

.css-lint-hinweis {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 8px 14px;
  border: 0;
  border-left: 3px solid #f59e0b;
  border-bottom: 1px solid #f1f3f6;
  background: none;
  color: #1f2430;
  text-align: left;
  cursor: pointer;

  &.is-info {
    border-left-color: #c7cbe8;
    color: #5b6270;
  }

  &:hover {
    background: #f8f9fb;
  }
}

.css-lint-zeile {
  flex: none;
  min-width: 56px;
  color: #8a919c;
  font-variant-numeric: tabular-nums;
}

.css-lint-text {
  word-break: break-word;
}

.css-lint-fuss {
  margin: 0;
  padding: 8px 14px;
  color: #8a919c;
  font-size: 12px;
}
</style>
